/**
 * Application Dossier Report
 * Displays and exports the full dossier response body.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import BusyOverlay from "../components/common/BusyOverlay";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import { emailService } from "../services/api/email.service";
import type { DossierResponse } from "../types/dossier.types";
import { getFullPath } from "../utils/getFullPath";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "../styles/pages/Reports.css";

type Primitive = string | number | boolean | null | undefined;
type GenericRecord = Record<string, unknown>;

const EXCLUDED_SECTION_KEYS = new Set(["createdby", "createdat"]);

const normalizeKey = (value: string) =>
  value.replace(/[\s_-]/g, "").toLowerCase();

const shouldDisplayKey = (key: string) =>
  !EXCLUDED_SECTION_KEYS.has(normalizeKey(key));

const isRecord = (value: unknown): value is GenericRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toTitle = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const stringifyValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "-";
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");

const PHOTO_KEYS = new Set(["profileimagepath", "photourl", "passportphoto"]);
const isPhotoKey = (key: string) => PHOTO_KEYS.has(normalizeKey(key));

// Fetches an image URL and returns a base64 data URL (for embedding in the PDF).
const loadImageDataUrl = async (url: string): Promise<string | null> => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const dataUrlFormat = (dataUrl: string): string => {
  const match = dataUrl.match(/^data:image\/(\w+);/);
  const fmt = (match?.[1] || "png").toUpperCase();
  return fmt === "JPG" ? "JPEG" : fmt;
};

const getObjectRows = (
  record: GenericRecord,
  options?: { skipNullValues?: boolean },
): [string, string][] =>
  Object.entries(record)
    .filter(([key]) => shouldDisplayKey(key))
    .filter(([, val]) => !(options?.skipNullValues && val === null))
    .map(([key, val]) => [toTitle(key), stringifyValue(val)]);

const getArrayColumnKeys = (rows: unknown[]) => {
  const keys = new Set<string>();
  rows.forEach((row) => {
    if (isRecord(row)) {
      Object.keys(row)
        .filter((key) => shouldDisplayKey(key))
        .forEach((key) => keys.add(key));
    }
  });
  return Array.from(keys);
};

const hasDisplayData = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasDisplayData(item));
  }

  if (isRecord(value)) {
    return Object.values(value).some((item) => hasDisplayData(item));
  }

  return true;
};

const ApplicationDossierReport: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dossier, setDossier] = useState<DossierResponse | null>(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);
    const handleResize = () => {
      setSidebarOpen(window.matchMedia("(min-width: 881px)").matches);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle(
      "Dossier Report",
      "Complete application record with all details",
    );
  }, [setPageTitle]);

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        setLoading(true);
        setError("");

        if (!applicationId) {
          setError("No application ID provided");
          return;
        }

        const appId = Number(applicationId);
        if (Number.isNaN(appId)) {
          setError("Invalid application ID");
          return;
        }

        const response = await certificateService.getApplicationDossier(appId);
        setDossier(response);
      } catch (err: any) {
        console.error("Error fetching dossier:", err);
        setError(err?.message || "Failed to load application dossier");
      } finally {
        setLoading(false);
      }
    };

    fetchDossier();
  }, [applicationId]);

  const sectionEntries = useMemo(() => {
    if (!dossier) return [] as Array<[string, unknown]>;
    return Object.entries(dossier).filter(([, sectionValue]) =>
      hasDisplayData(sectionValue),
    );
  }, [dossier]);

  const openDocumentPreview = (pathValue: unknown) => {
    if (typeof pathValue !== "string") return;
    const fullPath = getFullPath(pathValue);
    if (!fullPath) return;
    setDocumentPreviewUrl(fullPath);
  };

  const renderCellValue = (key: string, value: unknown) => {
    const normalizedKey = normalizeKey(key);

    // Render applicant photos (groom/bride etc.) as image thumbnails.
    if (
      normalizedKey === "profileimagepath" ||
      normalizedKey === "photourl" ||
      normalizedKey === "passportphoto"
    ) {
      const rawPath = typeof value === "string" ? value : "";
      const fullPath = getFullPath(rawPath);
      if (!fullPath) return "-";
      return (
        <img
          src={fullPath}
          alt="Photo"
          style={{
            width: "56px",
            height: "70px",
            objectFit: "cover",
            borderRadius: "4px",
            border: "1px solid #ddd",
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      );
    }

    if (normalizedKey === "documentpath") {
      const rawPath = typeof value === "string" ? value : "";
      const fullPath = getFullPath(rawPath);
      if (!fullPath) return "-";

      return (
        <button
          type="button"
          className="dossier-btn dossier-btn-secondary"
          onClick={() => openDocumentPreview(rawPath)}
          style={{ padding: "0.35rem 0.65rem", fontSize: "0.8rem" }}
        >
          View Document
        </button>
      );
    }

    return stringifyValue(value);
  };

  const getUiObjectEntries = (sectionName: string, record: GenericRecord) =>
    Object.entries(record)
      .filter(([key]) => shouldDisplayKey(key))
      .filter(
        ([, val]) =>
          !(sectionName.toLowerCase() === "application" && val === null),
      );

  const renderSectionTable = (sectionName: string, sectionValue: unknown) => {
    if (Array.isArray(sectionValue)) {
      if (sectionValue.length === 0) {
        return <p style={{ color: "#666" }}>No records found.</p>;
      }

      const columnKeys = getArrayColumnKeys(sectionValue);
      if (columnKeys.length === 0) {
        const hasRecordRows = sectionValue.some((item) => isRecord(item));
        if (hasRecordRows) {
          return <p style={{ color: "#666" }}>No records found.</p>;
        }

        return (
          <ul className="manual-list">
            {sectionValue.map((item, index) => (
              <li key={`${index}-${String(item)}`}>{stringifyValue(item)}</li>
            ))}
          </ul>
        );
      }

      return (
        <div className="table-wrapper">
          <table className="styled-table">
            <thead>
              <tr>
                {columnKeys.map((key) => (
                  <th key={key}>{toTitle(key)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sectionValue.map((item, rowIndex) => (
                <tr key={rowIndex}>
                  {columnKeys.map((key) => (
                    <td key={`${rowIndex}-${key}`} className="table-cell">
                      {isRecord(item) ? renderCellValue(key, item[key]) : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (isRecord(sectionValue)) {
      const entries = getUiObjectEntries(sectionName, sectionValue);
      if (entries.length === 0) {
        return null;
      }

      return (
        <table className="styled-table">
          <thead>
            <tr>
              <th style={{ width: "35%" }}>Field</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key}>
                <td className="table-header">{toTitle(key)}</td>
                <td className="table-cell">{renderCellValue(key, value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return <p>{stringifyValue(sectionValue as Primitive)}</p>;
  };

  const createSectionHtml = (sectionName: string, sectionValue: unknown) => {
    const title = toTitle(sectionName);

    if (Array.isArray(sectionValue)) {
      if (sectionValue.length === 0) {
        return `<div class="section"><h2>${escapeHtml(title)}</h2><p>No records found.</p></div>`;
      }

      const keys = getArrayColumnKeys(sectionValue);
      if (keys.length === 0) {
        const hasRecordRows = sectionValue.some((item) => isRecord(item));
        if (hasRecordRows) {
          return `<div class="section"><h2>${escapeHtml(title)}</h2><p>No records found.</p></div>`;
        }

        const items = sectionValue
          .map((item) => `<li>${escapeHtml(stringifyValue(item))}</li>`)
          .join("");
        return `<div class="section"><h2>${escapeHtml(title)}</h2><ul>${items}</ul></div>`;
      }

      const headerCells = keys
        .map((key) => `<th>${escapeHtml(toTitle(key))}</th>`)
        .join("");

      const bodyRows = sectionValue
        .map((item) => {
          const cells = keys
            .map((key) => {
              const value = isRecord(item) ? item[key] : "-";
              if (isPhotoKey(key) && typeof value === "string") {
                const fullPath = getFullPath(value);
                if (fullPath) {
                  return `<td><img src="${escapeHtml(
                    fullPath,
                  )}" alt="Photo" style="width:56px;height:70px;object-fit:cover;border-radius:4px;border:1px solid #ddd;" /></td>`;
                }
              }
              return `<td>${escapeHtml(stringifyValue(value))}</td>`;
            })
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");

      return `
        <div class="section">
          <h2>${escapeHtml(title)} (${sectionValue.length})</h2>
          <table class="styled-table">
            <thead><tr>${headerCells}</tr></thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </div>
      `;
    }

    if (isRecord(sectionValue)) {
      const rows = getObjectRows(sectionValue, {
        skipNullValues: sectionName.toLowerCase() === "application",
      });

      if (rows.length === 0) {
        return "";
      }

      const bodyRows = rows
        .map(
          ([field, value]) =>
            `<tr><td class="table-header">${escapeHtml(
              field,
            )}</td><td class="table-cell">${escapeHtml(value)}</td></tr>`,
        )
        .join("");

      return `
        <div class="section">
          <h2>${escapeHtml(title)}</h2>
          <table class="styled-table">
            <thead><tr><th>Field</th><th>Value</th></tr></thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </div>
      `;
    }

    return `<div class="section"><h2>${escapeHtml(title)}</h2><p>${escapeHtml(
      stringifyValue(sectionValue),
    )}</p></div>`;
  };

  const handlePrintDossier = () => {
    if (!dossier) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const app = dossier.application as GenericRecord | undefined;
    const formNo = app ? stringifyValue(app.formNo) : "-";
    const sectionHtml = sectionEntries
      .map(([name, value]) => createSectionHtml(name, value))
      .join("\n");

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Application Dossier - ${escapeHtml(formNo)}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; color: #111; }
          h1 { color: #1f3b6b; border-bottom: 2px solid #1f3b6b; padding-bottom: 8px; margin-bottom: 8px; }
          h2 { color: #204080; margin-top: 18px; margin-bottom: 8px; }
          table { width: 100%; border-collapse: collapse; margin: 12px 0; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: top; word-break: break-word; }
          th { background-color: #f5f7fb; }
          .header { background: #f8fafc; border: 1px solid #dde3ec; border-radius: 6px; padding: 12px; }
          .section { margin: 16px 0; page-break-inside: avoid; }
          @media print { body { margin: 0; padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Application Dossier Report</h1>
          <p><strong>Form No:</strong> ${escapeHtml(formNo)}</p>
          <p><strong>Generated:</strong> ${escapeHtml(new Date().toLocaleString())}</p>
        </div>

        ${sectionHtml}
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const buildDossierDoc = async (): Promise<{
    doc: jsPDF;
    formNo: string;
  } | null> => {
    if (!dossier) return null;

    // Pre-load every applicant photo as a base64 data URL so they can be embedded.
    const photoMap = new Map<string, string>();
    const photoUrls = new Set<string>();
    sectionEntries.forEach(([, sectionValue]) => {
      if (!Array.isArray(sectionValue)) return;
      sectionValue.forEach((item) => {
        if (!isRecord(item)) return;
        Object.entries(item).forEach(([key, val]) => {
          if (isPhotoKey(key) && typeof val === "string") {
            const full = getFullPath(val);
            if (full) photoUrls.add(full);
          }
        });
      });
    });
    await Promise.all(
      Array.from(photoUrls).map(async (url) => {
        const dataUrl = await loadImageDataUrl(url);
        if (dataUrl) photoMap.set(url, dataUrl);
      }),
    );

    const doc = new jsPDF({ unit: "mm", format: "a4" });
    let yPosition = 15;
    const pageHeight = doc.internal.pageSize.getHeight();

    const ensureSpace = (required = 20) => {
      if (yPosition + required > pageHeight - 10) {
        doc.addPage();
        yPosition = 15;
      }
    };

    const addHeading = (title: string) => {
      ensureSpace(14);
      doc.setFontSize(12);
      doc.text(title, 14, yPosition);
      yPosition += 6;
    };

    const addKeyValueTable = (rows: [string, string][]) => {
      autoTable(doc, {
        startY: yPosition,
        head: [["Field", "Value"]],
        body: rows,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 55 },
          1: { cellWidth: 125 },
        },
      });
      yPosition = (doc as any).lastAutoTable.finalY + 6;
    };

    const addArrayTable = (sectionValue: unknown[]) => {
      const keys = getArrayColumnKeys(sectionValue);
      if (keys.length === 0) {
        const hasRecordRows = sectionValue.some((item) => isRecord(item));
        if (hasRecordRows) {
          doc.setFontSize(9);
          doc.text("No records found.", 14, yPosition);
          yPosition += 6;
          return;
        }

        const listRows = sectionValue.map((item, index) => [
          String(index + 1),
          stringifyValue(item),
        ]);
        autoTable(doc, {
          startY: yPosition,
          head: [["#", "Value"]],
          body: listRows,
          theme: "grid",
          styles: { fontSize: 8, cellPadding: 2 },
        });
        yPosition = (doc as any).lastAutoTable.finalY + 6;
        return;
      }

      const photoColIndexes = new Set<number>();
      keys.forEach((key, idx) => {
        if (isPhotoKey(key)) photoColIndexes.add(idx);
      });

      const head = [keys.map((key) => toTitle(key))];
      const body = sectionValue.map((item) =>
        keys.map((key) => {
          if (isPhotoKey(key)) {
            const raw = isRecord(item) ? item[key] : "";
            const full = getFullPath(typeof raw === "string" ? raw : "");
            // Blank only when the image is available (drawn in didDrawCell);
            // otherwise leave a small placeholder instead of an empty cell.
            return full && photoMap.has(full) ? "" : "(photo)";
          }
          const value = isRecord(item) ? item[key] : "-";
          const text = stringifyValue(value);
          return text.length > 180 ? `${text.slice(0, 180)}...` : text;
        }),
      );

      const columnStyles: Record<number, Record<string, unknown>> = {};
      photoColIndexes.forEach((idx) => {
        columnStyles[idx] = { cellWidth: 24, minCellHeight: 26 };
      });

      autoTable(doc, {
        startY: yPosition,
        head,
        body,
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.5 },
        columnStyles,
        didDrawCell: (data: any) => {
          if (
            data.section !== "body" ||
            !photoColIndexes.has(data.column.index)
          ) {
            return;
          }
          const key = keys[data.column.index];
          const item = sectionValue[data.row.index];
          const raw = isRecord(item) ? item[key] : "";
          const full = getFullPath(typeof raw === "string" ? raw : "");
          const dataUrl = full ? photoMap.get(full) : undefined;
          if (!dataUrl) return;
          try {
            doc.addImage(
              dataUrl,
              dataUrlFormat(dataUrl),
              data.cell.x + 2,
              data.cell.y + 2,
              18,
              22,
            );
          } catch {
            /* ignore unsupported image */
          }
        },
      });
      yPosition = (doc as any).lastAutoTable.finalY + 6;
    };

    doc.setFontSize(16);
    doc.text("APPLICATION DOSSIER REPORT", 14, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPosition);
    yPosition += 6;

    sectionEntries.forEach(([sectionName, sectionValue]) => {
      addHeading(toTitle(sectionName));

      if (Array.isArray(sectionValue)) {
        if (sectionValue.length === 0) {
          doc.setFontSize(9);
          doc.text("No records found.", 14, yPosition);
          yPosition += 6;
          return;
        }
        addArrayTable(sectionValue);
        return;
      }

      if (isRecord(sectionValue)) {
        const rows = getObjectRows(sectionValue, {
          skipNullValues: sectionName.toLowerCase() === "application",
        });
        if (rows.length === 0) {
          return;
        }
        addKeyValueTable(rows);
        return;
      }

      doc.setFontSize(9);
      const lines = doc.splitTextToSize(stringifyValue(sectionValue), 180);
      ensureSpace(lines.length * 5 + 4);
      doc.text(lines, 14, yPosition);
      yPosition += lines.length * 5 + 2;
    });

    const app = dossier.application as GenericRecord | undefined;
    const formNo = app ? stringifyValue(app.formNo) : "application";
    return { doc, formNo };
  };

  const handleDownloadDossier = async () => {
    const built = await buildDossierDoc();
    if (!built) return;
    built.doc.save(`dossier-${built.formNo}.pdf`);
  };

  // ── Email the dossier PDF ──
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const openEmailModal = () => {
    const app = dossier?.application as GenericRecord | undefined;
    const formNo = app ? stringifyValue(app.formNo) : "";
    setEmailSubject(
      formNo && formNo !== "-"
        ? `Application Dossier Report — ${formNo}`
        : "Application Dossier Report",
    );
    setEmailMessage("Please find the attached application dossier report.");
    setShowEmailModal(true);
  };

  const closeEmailModal = () => {
    if (emailSending) return;
    setShowEmailModal(false);
  };

  const handleSendEmail = async () => {
    const recipients = emailTo.trim();
    if (!recipients) {
      showErrorToast("Please enter at least one recipient email address.");
      return;
    }
    // Light validation: every comma/semicolon-separated entry must look like an email.
    const parts = recipients
      .split(/[,;]/)
      .map((p) => p.trim())
      .filter(Boolean);
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = parts.find((p) => !emailPattern.test(p));
    if (invalid) {
      showErrorToast(`"${invalid}" is not a valid email address.`);
      return;
    }

    setEmailSending(true);
    try {
      const built = await buildDossierDoc();
      if (!built) {
        showErrorToast("The report is not ready yet. Please try again.");
        return;
      }
      // jsPDF can emit a base64 data URL string of the document.
      const dataUri = built.doc.output("datauristring");
      await emailService.sendReport({
        to: parts.join(","),
        subject: emailSubject.trim() || "Application Dossier Report",
        message: emailMessage.trim(),
        attachmentBase64: dataUri,
        attachmentFileName: `dossier-${built.formNo}.pdf`,
        attachmentContentType: "application/pdf",
      });
      showSuccessToast("Report emailed successfully.");
      setShowEmailModal(false);
      setEmailTo("");
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : "Failed to email the report.",
      );
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) {
    return (
      <BusyOverlay isVisible={true} message="Loading application dossier..." />
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />

      <main className="main-content">
        <div className="reports-page">
          {error ? (
            <div
              className="reports-error"
              style={{
                backgroundColor: "#fee",
                border: "1px solid #f99",
                color: "#c33",
                padding: "15px",
                borderRadius: "4px",
              }}
            >
              <strong>Error:</strong> {error}
              <button
                onClick={() => navigate("/reports/applications-summary")}
                className="dossier-btn dossier-btn-secondary"
                style={{ marginLeft: "20px" }}
              >
                Back to Reports
              </button>
            </div>
          ) : dossier ? (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div className="dossier-actions">
                  <button
                    onClick={handleDownloadDossier}
                    className="dossier-btn dossier-btn-primary"
                  >
                    Download PDF
                  </button>
                  <button
                    onClick={handlePrintDossier}
                    className="dossier-btn dossier-btn-primary"
                  >
                    Print
                  </button>
                  <button
                    onClick={openEmailModal}
                    className="dossier-btn dossier-btn-primary"
                  >
                    ✉️ Email
                  </button>
                </div>
              </div>

              {sectionEntries.map(([sectionName, sectionValue]) => (
                <div className="reports-section" key={sectionName}>
                  <h2>
                    {toTitle(sectionName)}
                    {Array.isArray(sectionValue)
                      ? ` (${sectionValue.length})`
                      : ""}
                  </h2>
                  {renderSectionTable(sectionName, sectionValue)}
                </div>
              ))}

              <div style={{ marginTop: "30px", textAlign: "center" }}>
                <button
                  onClick={() => navigate("/reports/applications-summary")}
                  className="dossier-btn dossier-btn-secondary"
                >
                  Back to Reports
                </button>
              </div>
            </>
          ) : (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <p>No dossier data available.</p>
              <button
                onClick={() => navigate("/reports/applications-summary")}
                className="dossier-btn dossier-btn-secondary"
              >
                Back to Reports
              </button>
            </div>
          )}

          {showEmailModal ? (
            <div
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1300,
                padding: "20px",
              }}
              onClick={closeEmailModal}
            >
              <div
                style={{
                  width: "min(520px, 96vw)",
                  background: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
                  overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 20px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <strong>✉️ Email Report</strong>
                  <button
                    type="button"
                    className="dossier-btn dossier-btn-secondary"
                    onClick={closeEmailModal}
                    disabled={emailSending}
                    style={{ padding: "4px 12px" }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ padding: "20px", display: "grid", gap: "14px" }}>
                  <label style={{ display: "grid", gap: "6px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 600 }}>
                      To <span style={{ color: "#dc3545" }}>*</span>
                    </span>
                    <input
                      type="text"
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      placeholder="name@example.com, another@example.com"
                      disabled={emailSending}
                      style={{
                        padding: "10px 12px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                    />
                    <span style={{ fontSize: "12px", color: "#8a9ab5" }}>
                      Separate multiple addresses with a comma.
                    </span>
                  </label>

                  <label style={{ display: "grid", gap: "6px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 600 }}>
                      Subject
                    </span>
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      disabled={emailSending}
                      style={{
                        padding: "10px 12px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        fontSize: "14px",
                      }}
                    />
                  </label>

                  <label style={{ display: "grid", gap: "6px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 600 }}>
                      Message
                    </span>
                    <textarea
                      value={emailMessage}
                      onChange={(e) => setEmailMessage(e.target.value)}
                      rows={4}
                      disabled={emailSending}
                      style={{
                        padding: "10px 12px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        fontSize: "14px",
                        resize: "vertical",
                      }}
                    />
                  </label>

                  <div
                    style={{
                      fontSize: "13px",
                      color: "#475569",
                      background: "#f1f5f9",
                      borderRadius: "6px",
                      padding: "10px 12px",
                    }}
                  >
                    📎 The dossier will be attached as a PDF.
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    padding: "16px 20px",
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <button
                    type="button"
                    className="dossier-btn dossier-btn-secondary"
                    onClick={closeEmailModal}
                    disabled={emailSending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="dossier-btn dossier-btn-primary"
                    onClick={handleSendEmail}
                    disabled={emailSending}
                  >
                    {emailSending ? "Sending…" : "Send Email"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {documentPreviewUrl ? (
            <div
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1200,
                padding: "20px",
              }}
            >
              <div
                style={{
                  width: "min(1100px, 96vw)",
                  height: "min(820px, 90vh)",
                  background: "#fff",
                  borderRadius: "12px",
                  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 16px",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <strong>Document Preview</strong>
                  <div className="dossier-actions" style={{ width: "auto" }}>
                    <button
                      type="button"
                      className="dossier-btn dossier-btn-secondary"
                      onClick={() => window.open(documentPreviewUrl, "_blank")}
                    >
                      Open In New Tab
                    </button>
                    <button
                      type="button"
                      className="dossier-btn dossier-btn-secondary"
                      onClick={() => setDocumentPreviewUrl(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>

                <iframe
                  title="Document Preview"
                  src={documentPreviewUrl}
                  style={{ border: "none", width: "100%", height: "100%" }}
                />
              </div>
            </div>
          ) : null}
        </div>

        <Footer />
      </main>
    </div>
  );
};

export default ApplicationDossierReport;
